import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  AttachFile,
  MyLocation,
  AddShoppingCart,
  ViewList,
  ContactPhone,
  Videocam,
  VideoCall,
  HowToVote,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

/**
 * Botão "+" com menu: anexo, produtos, localização, vídeo (conforme props).
 */
function ComposeAttachMenu({
  openFilePicker,
  fileSendDisabled,
  fileSendDisabledHelp,
  showAttachProducts,
  onOpenProductPicker,
  productsSendDisabled,
  productsSendDisabledHelp,
  showTemplates,
  onOpenTemplates,
  templatesDisabled,
  templatesDisabledHelp,
  showLocation,
  onSendCurrentLocation,
  locationSendDisabled,
  locationSending,
  locationSendDisabledHelp,
  showShareContact,
  onShareContact,
  contactSendDisabled,
  contactSendDisabledHelp,
  showStartVideo,
  onStartVideo,
  videoSending,
  videoSendDisabled,
  videoSendDisabledHelp,
  videoMenuLabel,
  showJoinVideo,
  onJoinVideo,
  joinVideoSending,
  showGoogleMeet,
  onStartGoogleMeet,
  googleMeetSending,
  googleMeetDisabled,
  googleMeetDisabledHelp,
  showPoll = false,
  onStartPoll,
  pollDisabled = false,
  pollDisabledHelp = null,
  size = "medium",
}) {
  const { t } = useTranslation("chat");
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const wasVideoBusyRef = useRef(false);
  const open = Boolean(anchorEl);
  const videoBusy = Boolean(videoSending || joinVideoSending || googleMeetSending);

  const close = useCallback(() => setAnchorEl(null), []);

  // Fecha o menu só quando o fluxo de vídeo terminar (mantém o item com loading visível).
  useEffect(() => {
    if (wasVideoBusyRef.current && !videoBusy) {
      close();
    }
    wasVideoBusyRef.current = videoBusy;
  }, [videoBusy, close]);

  const iconSize = size === "small" ? "small" : "medium";
  const attachBusy = Boolean(videoSending || joinVideoSending || googleMeetSending || locationSending);

  return (
    <>
      <Tooltip
        title={
          videoSending
            ? t("video.preparingInvite", "Preparando convite de vídeo…")
            : joinVideoSending
              ? t("video.joiningCall", "Abrindo a chamada…")
              : googleMeetSending
                ? t("video.sending", "Criando reunião…")
                : t("input.menuAttachTooltip")
        }
      >
        <span>
          <IconButton
            ref={buttonRef}
            size={size === "small" ? "small" : "medium"}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            disabled={attachBusy}
            aria-label={t("input.menuAttachAria")}
            aria-haspopup="true"
            aria-expanded={open}
            aria-busy={attachBusy}
          >
            {attachBusy ? (
              <CircularProgress size={size === "small" ? 16 : 18} thickness={5} />
            ) : (
              <Add fontSize={iconSize} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          if (videoBusy) return;
          close();
        }}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Tooltip title={fileSendDisabled ? fileSendDisabledHelp || "" : ""}>
          <span>
            <MenuItem
              disabled={fileSendDisabled}
              onClick={() => {
                close();
                openFilePicker?.();
              }}
            >
              <ListItemIcon>
                <AttachFile fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("input.menuAttachFile")} />
            </MenuItem>
          </span>
        </Tooltip>
        {showAttachProducts ? (
          <Tooltip title={productsSendDisabled ? productsSendDisabledHelp || "" : ""}>
            <span>
              <MenuItem
                disabled={productsSendDisabled}
                onClick={() => {
                  close();
                  onOpenProductPicker?.();
                }}
              >
                <ListItemIcon>
                  <AddShoppingCart fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("input.menuAttachProducts")} />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {showTemplates ? (
          <Tooltip title={templatesDisabled ? templatesDisabledHelp || "" : ""}>
            <span>
              <MenuItem
                disabled={templatesDisabled}
                onClick={() => {
                  close();
                  onOpenTemplates?.();
                }}
              >
                <ListItemIcon>
                  <ViewList fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("input.menuTemplates")} />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {showLocation ? (
          <Tooltip title={locationSendDisabled ? locationSendDisabledHelp || "" : ""}>
            <span>
              <MenuItem
                disabled={locationSendDisabled || locationSending}
                onClick={() => {
                  close();
                  onSendCurrentLocation?.();
                }}
              >
                <ListItemIcon>
                  <MyLocation fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("location.sendCurrent")} />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {showPoll ? (
          <Tooltip title={pollDisabled ? pollDisabledHelp || "" : ""}>
            <span>
              <MenuItem
                disabled={pollDisabled}
                onClick={() => {
                  close();
                  onStartPoll?.();
                }}
              >
                <ListItemIcon>
                  <HowToVote fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("input.menuPoll", "Enquete")} />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {showShareContact ? (
          <Tooltip title={contactSendDisabled ? contactSendDisabledHelp || "" : ""}>
            <span>
              <MenuItem
                disabled={contactSendDisabled}
                onClick={() => {
                  close();
                  onShareContact?.();
                }}
              >
                <ListItemIcon>
                  <ContactPhone fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("shareContact.menuItem")} />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {showJoinVideo ? (
          <MenuItem
            disabled={joinVideoSending || videoSending || googleMeetSending}
            onClick={() => {
              onJoinVideo?.();
            }}
          >
            <ListItemIcon>
              {joinVideoSending ? (
                <CircularProgress size={18} thickness={5} />
              ) : (
                <Videocam fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                joinVideoSending
                  ? t("video.joiningCall", "Abrindo a chamada…")
                  : t("video.joinActive", "Entrar na chamada")
              }
            />
          </MenuItem>
        ) : null}
        {showStartVideo ? (
          <Tooltip title={videoSendDisabled && !videoSending ? videoSendDisabledHelp || "" : ""}>
            <span>
              <MenuItem
                disabled={videoSendDisabled || videoSending || joinVideoSending || googleMeetSending}
                onClick={() => {
                  if (videoSendDisabled || videoSending) return;
                  onStartVideo?.();
                }}
              >
                <ListItemIcon>
                  {videoSending ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <Videocam fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    videoSending
                      ? t("video.preparingInvite", "Preparando convite de vídeo…")
                      : videoMenuLabel ||
                        t("video.startXpro", "Convidar para vídeo XPro")
                  }
                />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {showGoogleMeet ? (
          <Tooltip title={googleMeetDisabled && !googleMeetSending ? googleMeetDisabledHelp || "" : ""}>
            <span>
              <MenuItem
                disabled={googleMeetDisabled || googleMeetSending || videoSending || joinVideoSending}
                onClick={() => {
                  if (googleMeetDisabled || googleMeetSending) return;
                  onStartGoogleMeet?.();
                }}
              >
                <ListItemIcon>
                  {googleMeetSending ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <VideoCall fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    googleMeetSending
                      ? t("video.sending", "Criando reunião…")
                      : t("video.startMeet", "Criar Google Meet")
                  }
                />
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
      </Menu>
    </>
  );
}

export default ComposeAttachMenu;
